import {autocompleteClasses, Popper, styled} from '@mui/material';
import React from 'react';
import {FixedSizeList, ListChildComponentProps} from 'react-window';

import {LightSelectFilter, LightSelectFilterProps} from './LightSelectFilter';

export type LightSelectFilterVirtualizedProps<T = any> = Omit<LightSelectFilterProps<T>, 'PopperComponent' | 'groupBy'>;
export const LightSelectFilterVirtualized = <T,>(props: LightSelectFilterVirtualizedProps<T>) => {
  return (
    <LightSelectFilter
      {...props}
      disableListWrap
      isVirtualized
      PopperComponent={StyledPopper}
      ListboxComponent={VirtualizedListbox as unknown as React.JSXElementConstructor<React.HTMLAttributes<HTMLElement>>}
    />
  );
};

// copy paste from https://mui.com/material-ui/react-autocomplete/#virtualization + added type info + edits from ChatGPT...
const ITEM_HEIGHT = 30;
// const GROUP_HEIGHT = 30; // NOTE: we won't support groups
const LISTBOX_PADDING_BLOCK = 8;

const renderRow = ({data, index, style}: ListChildComponentProps) => {
  const item = data[index];
  const itemProps = item.props;

  return React.cloneElement(item, {
    ...itemProps,
    style: {
      ...itemProps.style,
      ...style,
      top: (style.top as number) + LISTBOX_PADDING_BLOCK,
    },
  });
};

const OuterElementContext = React.createContext({});
const OuterElementType = React.forwardRef<HTMLDivElement>(function OuterElementType(props, ref) {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

type VirtualizedListboxProps = React.HTMLAttributes<HTMLElement>;
const VirtualizedListbox = React.forwardRef<HTMLDivElement, VirtualizedListboxProps>(function VirtualizedListbox(props, ref) {
  const {children, ...extraProps} = props;

  const itemData = React.Children.toArray(children);
  const itemCount = itemData.length;

  return (
    <div ref={ref} style={{padding: 0}}>
      <OuterElementContext.Provider value={extraProps}>
        <FixedSizeList
          height={Math.min(8, itemCount) * ITEM_HEIGHT + 2 * LISTBOX_PADDING_BLOCK}
          itemCount={itemCount}
          itemSize={ITEM_HEIGHT}
          itemData={itemData}
          width='100%'
          outerRef={ref}
          outerElementType={OuterElementType}
        >
          {renderRow}
        </FixedSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

const StyledPopper = styled(Popper)({
  [`& .${autocompleteClasses.listbox}`]: {
    'boxSizing': 'border-box',
    '& ul': {
      padding: 0,
      margin: 0,
    },
  },
});
